import React, { useState } from 'react';
import { normalizeTeamName } from '../utils/teamUtils';

const PlayerSplits = ({ players, playerId, selectedStat, activeFilters, isFilterActive, toggleFilter, getStatName, selectedSeasons }) => {
  const [selectedCategory, setSelectedCategory] = useState('result');

  if (!players || !playerId) return null;
  
  // Filter games for this player and selected seasons
  const playerGames = players.filter(p => 
    p.player_name === playerId && 
    selectedSeasons.includes(p.year)
  );
  
  // Apply finals filtering if needed
  const filteredPlayerGames = playerGames.filter(game => {
    // Check if this is a finals round (rounds 25-28)
    const roundNum = parseInt(game.round?.replace(/\D/g, '')) || 0;
    const isFinalsRound = roundNum >= 25 && roundNum <= 28;

    // Skip if it's a finals round and we're excluding finals
    if (isFinalsRound && activeFilters.some(f => f.type === 'excludeFinals')) {
      return false;
    }
    
    return true;
  });
  
  if (filteredPlayerGames.length === 0) return (
    <div className="text-center text-gray-500 dark:text-gray-400 py-4">
      No split data available
    </div>
  );

  // Define the stats we want to show
  const stats = [
    { key: 'performance_points', label: 'PP' } ,
    { key: 'tries', label: 'Tries' },
    { key: 'points', label: 'Points' },
    { key: 'all_run_metres', label: 'Run Metres' },
    { key: 'tackles_made', label: 'Tackles' },
    { key: 'line_breaks', label: 'Line Breaks' },
    { key: 'try_assists', label: 'Try Assists' },
    { key: 'tackle_breaks', label: 'Tackle Breaks' },
    { key: 'offloads', label: 'Offloads' },
    { key: 'errors', label: 'Errors' },
    { key: 'mins_played', label: 'Minutes' },

  ];

  // Calculate splits
  const calculateSplits = (category) => {
    let splits = {};

    switch (category) {
      case 'result':
        splits = {
          overall: { games: filteredPlayerGames },
          wins: { games: filteredPlayerGames.filter(g => {
            const isHome = g.is_home_game;
            const homeScore = parseInt(g.home_score);
            const awayScore = parseInt(g.away_score);
            return isHome ? homeScore > awayScore : awayScore > homeScore;
          })},
          losses: { games: filteredPlayerGames.filter(g => {
            const isHome = g.is_home_game;
            const homeScore = parseInt(g.home_score);
            const awayScore = parseInt(g.away_score);
            return isHome ? homeScore < awayScore : awayScore < homeScore;
          })},
          margin20: { games: filteredPlayerGames.filter(g => 
            Math.abs(parseInt(g.home_score) - parseInt(g.away_score)) <= 20
          )},
          blowout: { games: filteredPlayerGames.filter(g => 
            Math.abs(parseInt(g.home_score) - parseInt(g.away_score)) > 20
          )}
        };
        break;

      case 'venue':
        const venues = [...new Set(filteredPlayerGames.map(g => g.venue))];
        splits.overall = { games: filteredPlayerGames };
        venues.forEach(venue => {
          splits[venue] = { games: filteredPlayerGames.filter(g => g.venue === venue) };
        });
        break;

      case 'location':
        splits = {
          overall: { games: filteredPlayerGames },
          home: { games: filteredPlayerGames.filter(g => g.is_home_game === true) },
          away: { games: filteredPlayerGames.filter(g => g.is_home_game === false) }
        };
        break;

    }

    // Calculate averages for each split
    Object.keys(splits).forEach(splitKey => {
      const games = splits[splitKey].games;
      stats.forEach(stat => {
        if (stat.key === 'performance_points') {
          // Calculate performance points for each game and then average
          const values = games.map(g => {
            let pp = 0;
            // 4pts per point scored
            pp += (parseFloat(g['points']) || 0) * 4;
            // 10pts per try assist
            pp += (parseFloat(g['try_assists']) || 0) * 10;
            // 1pt per 10-meter run (rounded down to the nearest 10)
            const runMeters = parseFloat(g['all_run_metres']) || 0;
            pp += Math.floor(runMeters / 10);
            // 5pts per line break
            pp += (parseFloat(g['line_breaks']) || 0) * 5;
            // 1pt per tackle
            pp += (parseFloat(g['tackles_made']) || 0);
            return pp;
          });
          const sum = values.reduce((a, b) => a + b, 0);
          splits[splitKey][stat.key] = games.length > 0 ? (sum / games.length).toFixed(1) : '-';
        } else {
          const values = games.map(g => parseFloat(g[stat.key]) || 0);
          const sum = values.reduce((a, b) => a + b, 0);
          splits[splitKey][stat.key] = games.length > 0 ? (sum / games.length).toFixed(1) : '-';
        }
      });
    });

    return splits;
  };

  const splits = calculateSplits(selectedCategory);

  // Define categories and their rows
  const categories = {
    result: [
      { key: 'overall', label: 'Overall', type: 'all' },
      { key: 'wins', label: 'In Wins', type: 'wins' },
      { key: 'losses', label: 'In Losses', type: 'losses' },
      { key: 'margin20', label: 'Close Games', type: 'margin20' },
      { key: 'blowout', label: 'Blowouts', type: 'blowout' }
    ],
    location: [
      { key: 'overall', label: 'Overall', type: 'all' },
      { key: 'home', label: 'Home', type: 'home' },
      { key: 'away', label: 'Away', type: 'away' }
    ]
  };

  // Add dynamic categories for venue and weather
  if (selectedCategory === 'venue') {
    categories.venue = [
      { key: 'overall', label: 'Overall', type: 'all' },
      ...Object.keys(splits)
        .filter(key => key !== 'overall')
        .map(venue => ({
          key: venue,
          label: venue,
          type: 'venue',
          value: venue
        }))
    ];
  }

  const categoryLabels = {
    result: 'Match Result',
    location: 'Location',
    venue: 'Venue',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-900 dark:text-white flex-shrink-0">Splits</span>
          <div className="overflow-x-auto">
            <div className="flex space-x-1">
              {Object.keys(categoryLabels).map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-2.5 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                    selectedCategory === category
                      ? 'bg-blue-600 text-white dark:bg-blue-500'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {categoryLabels[category]}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="p-3">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Situation</th>
                <th className="py-2 px-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">Games</th>
                {stats.map(stat => (
                  <th key={stat.key} className="py-2 px-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">
                    {stat.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {categories[selectedCategory]?.map((row, index) => (
                <tr 
                  key={row.key} 
                  className={`
                    cursor-pointer
                    ${index !== categories[selectedCategory].length - 1 ? 'border-b border-gray-100 dark:border-gray-700' : ''}
                    ${isFilterActive(row.type, row.value) 
                      ? 'bg-blue-50/50 dark:bg-blue-900/20' 
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}
                  `}
                  onClick={() => toggleFilter(row.type, row.value)}
                >
                  <td className="py-2 px-3 text-xs">
                    <div className="flex items-center">
                      <span className={isFilterActive(row.type, row.value) 
                        ? 'font-medium text-blue-600 dark:text-blue-400' 
                        : 'text-gray-900 dark:text-gray-300'}>
                        {row.label}
                      </span>
                      {isFilterActive(row.type, row.value) && (
                        <svg className="ml-1.5 w-3 h-3 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-3 text-center text-xs text-gray-600 dark:text-gray-400">{splits[row.key].games.length}</td>
                  {stats.map(stat => (
                    <td 
                      key={stat.key} 
                      className={`py-2 px-3 text-center text-xs ${
                        row.key !== 'overall' && parseFloat(splits[row.key][stat.key]) > parseFloat(splits['overall'][stat.key])
                          ? 'text-green-600 dark:text-green-400 font-medium'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {splits[row.key][stat.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PlayerSplits; 